import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Camera, Upload, Volume2, Pause, Play, SkipForward, SkipBack, Square, X,
  Loader2, Sparkles, Trash2, Check, GripVertical, ListMusic, Plus, Moon, Sun,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPENAI_VOICES = [
  { id: 'shimmer', label: 'Shimmer' },
  { id: 'nova',    label: 'Nova' },
  { id: 'coral',   label: 'Coral' },
  { id: 'sage',    label: 'Sage' },
  { id: 'fable',   label: 'Fable' },
  { id: 'alloy',   label: 'Alloy' },
  { id: 'echo',    label: 'Echo' },
  { id: 'ash',     label: 'Ash' },
  { id: 'onyx',    label: 'Onyx' },
];

const SPEEDS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

async function compressImage(file, maxEdge = 1600, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export default function ReaderQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false); // OCR-add in progress
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);

  // Player
  const [voice, setVoice] = useState(() => localStorage.getItem('reader_q_voice') || 'shimmer');
  const [model, setModel] = useState(() => localStorage.getItem('reader_q_model') || 'tts-1');
  const [speed, setSpeed] = useState(() => parseFloat(localStorage.getItem('reader_q_speed') || '1.0'));
  const [nightMode, setNightMode] = useState(() => localStorage.getItem('reader_q_night') === '1');
  const [currentId, setCurrentId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const audioRef = useRef(null);
  // Pre-fetched next blob URL for smooth advance
  const nextBlobRef = useRef({ id: null, url: null });

  // Drag state
  const dragIdRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  useEffect(() => { localStorage.setItem('reader_q_voice', voice); }, [voice]);
  useEffect(() => { localStorage.setItem('reader_q_model', model); }, [model]);
  useEffect(() => { localStorage.setItem('reader_q_speed', String(speed)); }, [speed]);
  useEffect(() => { localStorage.setItem('reader_q_night', nightMode ? '1' : '0'); }, [nightMode]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, audioUrl]);

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (nextBlobRef.current.url) URL.revokeObjectURL(nextBlobRef.current.url);
  }, [audioUrl]);

  // ---------- Night mode: Wake Lock + Media Session ----------
  const wakeLockRef = useRef(null);

  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      if (wakeLockRef.current) return;
      const sentinel = await navigator.wakeLock.request('screen');
      wakeLockRef.current = sentinel;
      sentinel.addEventListener('release', () => {
        if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
      });
    } catch { /* user can deny — silent */ }
  }, []);

  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release(); } catch { /* ignore */ }
    wakeLockRef.current = null;
  }, []);

  // Re-acquire wake lock when tab returns to foreground
  useEffect(() => {
    if (!nightMode) return;
    const onVis = () => {
      if (document.visibilityState === 'visible' && playing) acquireWakeLock();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [nightMode, playing, acquireWakeLock]);

  // Acquire/release based on play state + night mode
  useEffect(() => {
    if (nightMode && playing) acquireWakeLock();
    else releaseWakeLock();
    return () => { /* cleanup handled by next effect run */ };
  }, [nightMode, playing, acquireWakeLock, releaseWakeLock]);

  // Cleanup on unmount
  useEffect(() => () => releaseWakeLock(), [releaseWakeLock]);

  // Media Session — title on lock screen + native play/pause/skip controls
  const currentItemForMedia = items.find((x) => x.id === currentId);
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentItemForMedia) {
      try { navigator.mediaSession.metadata = null; } catch { /* ignore */ }
      return;
    }
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentItemForMedia.title || 'Läs upp',
        artist: 'Focus Critter — Läs upp',
        album: 'Läs upp-kö',
      });
    } catch { /* ignore */ }
  }, [currentItemForMedia]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const safeSet = (action, handler) => {
      try { ms.setActionHandler(action, handler); } catch { /* unsupported action */ }
    };
    safeSet('play', () => { if (audioRef.current && audioRef.current.paused) audioRef.current.play(); });
    safeSet('pause', () => { if (audioRef.current && !audioRef.current.paused) audioRef.current.pause(); });
    safeSet('previoustrack', () => handleSkipPrev());
    safeSet('nexttrack', () => handleSkipNext());
    safeSet('stop', () => { stopPlayback(); setCurrentId(null); });
    return () => {
      ['play', 'pause', 'previoustrack', 'nexttrack', 'stop'].forEach((a) => safeSet(a, null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, currentId]);

  // Reflect playback state to OS
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try { navigator.mediaSession.playbackState = playing ? 'playing' : (currentId ? 'paused' : 'none'); }
    catch { /* ignore */ }
  }, [playing, currentId]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reader/queue`, { withCredentials: true });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---------- Add to queue ----------
  const handlePickFile = () => fileRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      toast.error('Bara JPG, PNG eller WEBP funkar 🌸');
      return;
    }
    try {
      const compressed = await compressImage(file);
      await ocrAndAdd(compressed);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const ocrAndAdd = async (dataUrl) => {
    setAdding(true);
    try {
      const { data } = await axios.post(
        `${API}/reader/queue/from-ocr`,
        { image_base64: dataUrl },
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(`✨ La till "${data.title}" i kön`);
      setItems((prev) => [...prev, data]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Kunde inte lägga till: ${detail}`);
    } finally {
      setAdding(false);
    }
  };

  const removeItem = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    if (currentId === id) {
      stopPlayback();
      setCurrentId(null);
    }
    try { await axios.delete(`${API}/reader/queue/${id}`, { withCredentials: true }); }
    catch { toast.error('Kunde inte ta bort'); load(); }
  };

  const togglePlayed = async (id, played) => {
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, played } : x));
    try {
      await axios.patch(`${API}/reader/queue/${id}`, { played }, { withCredentials: true });
    } catch { load(); }
  };

  const clearPlayed = async () => {
    try {
      await axios.post(`${API}/reader/queue/clear-played`, {}, { withCredentials: true });
      setItems((prev) => prev.filter((x) => !x.played));
      toast.success('Spelade rensade ✨');
    } catch { /* ignore */ }
  };

  const clearAll = async () => {
    if (!window.confirm('Töm hela kön?')) return;
    try {
      await axios.delete(`${API}/reader/queue`, { withCredentials: true });
      setItems([]);
      stopPlayback();
      setCurrentId(null);
      toast.success('Kön är tom 🌸');
    } catch { /* ignore */ }
  };

  // ---------- TTS ----------
  const fetchTtsBlobUrl = useCallback(async (text) => {
    const resp = await axios.post(
      `${API}/reader/tts`,
      { text, voice, model, speed: 1.0 },
      { withCredentials: true, responseType: 'blob', timeout: 90000 },
    );
    return URL.createObjectURL(resp.data);
  }, [voice, model]);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (nextBlobRef.current.url) {
      URL.revokeObjectURL(nextBlobRef.current.url);
      nextBlobRef.current = { id: null, url: null };
    }
  }, [audioUrl]);

  // Play a specific item by id (or next pending if not given)
  const pendingItems = useMemo(() => items.filter((x) => !x.played), [items]);

  const playItem = useCallback(async (id) => {
    const target = items.find((x) => x.id === id);
    if (!target) return;
    setGenerating(true);
    // If we already pre-fetched this id, use it
    let url;
    if (nextBlobRef.current.id === id && nextBlobRef.current.url) {
      url = nextBlobRef.current.url;
      nextBlobRef.current = { id: null, url: null };
    } else {
      try {
        url = await fetchTtsBlobUrl(target.text);
      } catch (e) {
        toast.error(`Kunde inte skapa ljud: ${e?.response?.data?.detail || e.message}`);
        setGenerating(false);
        return;
      }
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setCurrentId(id);
    setAudioUrl(url);
    setGenerating(false);
    // Auto-play
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.playbackRate = speed;
        audioRef.current.play().catch(() => {});
      }
    }, 50);
    // Pre-fetch next pending item (different from this one)
    const idx = pendingItems.findIndex((x) => x.id === id);
    const next = idx >= 0 ? pendingItems[idx + 1] : null;
    if (next && next.id !== nextBlobRef.current.id) {
      try {
        const nUrl = await fetchTtsBlobUrl(next.text);
        if (nextBlobRef.current.url) URL.revokeObjectURL(nextBlobRef.current.url);
        nextBlobRef.current = { id: next.id, url: nUrl };
      } catch { /* ignore prefetch failure */ }
    }
  }, [items, pendingItems, audioUrl, fetchTtsBlobUrl, speed]);

  const handlePlayQueue = () => {
    if (!pendingItems.length) {
      toast.info('Kön är tom — lägg till papper först 📸');
      return;
    }
    playItem(pendingItems[0].id);
  };

  const handlePlayPauseToggle = () => {
    if (!audioRef.current) {
      if (currentId) playItem(currentId);
      return;
    }
    if (audioRef.current.paused) audioRef.current.play();
    else audioRef.current.pause();
  };

  const handleSkipNext = async () => {
    if (!currentId) return;
    await togglePlayed(currentId, true);
    const idx = pendingItems.findIndex((x) => x.id === currentId);
    const next = idx >= 0 ? pendingItems[idx + 1] : null;
    if (next) playItem(next.id);
    else { stopPlayback(); setCurrentId(null); toast.success('🎉 Hela kön klar!'); }
  };

  const handleSkipPrev = () => {
    const idx = items.findIndex((x) => x.id === currentId);
    const prev = idx > 0 ? items[idx - 1] : null;
    if (prev) playItem(prev.id);
  };

  const onTrackEnded = async () => {
    if (currentId) await togglePlayed(currentId, true);
    const idx = items.findIndex((x) => x.id === currentId);
    const next = items.slice(idx + 1).find((x) => !x.played);
    if (next) playItem(next.id);
    else { stopPlayback(); setCurrentId(null); toast.success('🎉 Hela kön klar!'); }
  };

  // ---------- Drag-and-drop reorder ----------
  const handleDragStart = (id) => { dragIdRef.current = id; };
  const handleDragOver = (e, id) => { e.preventDefault(); setDragOverId(id); };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const from = dragIdRef.current;
    dragIdRef.current = null;
    setDragOverId(null);
    if (!from || from === targetId) return;
    const fromIdx = items.findIndex((x) => x.id === from);
    const toIdx = items.findIndex((x) => x.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    try {
      await axios.post(`${API}/reader/queue/reorder`,
        { order: next.map((x) => x.id) },
        { withCredentials: true });
    } catch { load(); }
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const currentItem = items.find((x) => x.id === currentId);

  return (
    <div className="space-y-4" data-testid="reader-queue">
      {/* Add to queue */}
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFF5E6] to-[#FFE8F5]">
        <div className="flex items-center gap-2 mb-3">
          <ListMusic className="w-5 h-5" />
          <p className="font-bold">Läs upp-kö 🎧</p>
          <span className="text-[11px] text-gray-600 ml-auto">
            {items.length} totalt · {pendingItems.length} kvar
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <button
            onClick={() => setCameraOpen(true)}
            disabled={adding}
            className="neo-btn p-3 bg-[#A2D2FF] flex items-center justify-center gap-2 font-bold text-sm"
            data-testid="queue-add-camera-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span>Ta foto</span>
          </button>
          <button
            onClick={handlePickFile}
            disabled={adding}
            className="neo-btn p-3 bg-[#B9FBC0] flex items-center justify-center gap-2 font-bold text-sm"
            data-testid="queue-add-upload-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span>Ladda upp</span>
          </button>
          <button
            onClick={handlePlayQueue}
            disabled={!pendingItems.length || generating}
            className="neo-btn p-3 bg-[#FFC8DD] flex items-center justify-center gap-2 font-bold text-sm"
            data-testid="queue-play-all-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            <span>{pendingItems.length ? `Spela kön (${pendingItems.length})` : 'Inget att spela'}</span>
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleFileChange}
          className="hidden"
          data-testid="queue-file-input"
        />
      </div>

      {/* Voice + speed pickers */}
      <div className="neo-card p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold">🎙️ Röst</p>
          <button
            onClick={() => setNightMode((v) => !v)}
            className={`neo-btn px-2 py-1 text-[11px] font-bold flex items-center gap-1 ${nightMode ? 'bg-[#C8B6FF]' : 'bg-white'}`}
            title={nightMode ? 'Nattlampsläge PÅ — skärmen släcks inte och låsskärmen visar titeln' : 'Slå på nattlampsläge'}
            data-testid="queue-night-mode-toggle"
          >
            {nightMode ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
            <span>{nightMode ? 'Nattlampsläge' : 'Lyssna med släckt skärm'}</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {OPENAI_VOICES.map((v) => (
            <button
              key={v.id}
              onClick={() => setVoice(v.id)}
              className={`neo-btn px-2 py-1 text-[11px] ${voice === v.id ? 'bg-[#FFE8A3]' : 'bg-white'}`}
              data-testid={`queue-voice-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-bold">Kvalitet:</span>
          <button onClick={() => setModel('tts-1')} className={`neo-btn px-2 py-0.5 ${model === 'tts-1' ? 'bg-[#B9FBC0]' : 'bg-white'}`}>Snabb</button>
          <button onClick={() => setModel('tts-1-hd')} className={`neo-btn px-2 py-0.5 ${model === 'tts-1-hd' ? 'bg-[#B9FBC0]' : 'bg-white'}`}>HD</button>
          <span className="font-bold ml-3">Hastighet:</span>
          {SPEEDS.map((s) => (
            <button key={s} onClick={() => setSpeed(s)} className={`neo-btn px-1.5 py-0.5 ${speed === s ? 'bg-[#A2D2FF]' : 'bg-white'}`} data-testid={`queue-speed-${s}`}>
              {s}x
            </button>
          ))}
        </div>
      </div>

      {/* Now playing */}
      {currentItem && (
        <div className="neo-card p-4 bg-[#FFFEF5] sticky top-2 z-10" data-testid="queue-now-playing">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" />
            <p className="text-[11px] font-bold uppercase opacity-60">Nu spelas</p>
            <p className="font-bold text-sm truncate flex-1">{currentItem.title}</p>
          </div>
          <audio
            ref={audioRef}
            src={audioUrl || undefined}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={onTrackEnded}
            onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
            onTimeUpdate={(e) => setPosition(e.target.currentTime || 0)}
            data-testid="queue-audio-element"
          />
          <div className="flex items-center gap-2">
            <button onClick={handleSkipPrev} className="neo-btn p-2 bg-white" data-testid="queue-prev-btn"><SkipBack className="w-4 h-4" /></button>
            <button onClick={handlePlayPauseToggle} className="neo-btn p-2 bg-[#FFC8DD]" data-testid="queue-playpause-btn">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={handleSkipNext} className="neo-btn p-2 bg-white" data-testid="queue-next-btn"><SkipForward className="w-4 h-4" /></button>
            <button onClick={() => { stopPlayback(); setCurrentId(null); }} className="neo-btn p-2 bg-white" data-testid="queue-stop-btn"><Square className="w-4 h-4" /></button>
            <input
              type="range"
              min={0}
              max={Math.max(0.01, duration)}
              step={0.1}
              value={position}
              onChange={(e) => { if (audioRef.current) { audioRef.current.currentTime = parseFloat(e.target.value); } }}
              className="flex-1 accent-[#FFC8DD]"
              data-testid="queue-scrub"
            />
            <span className="text-[11px] tabular-nums w-16 text-right">{fmt(position)} / {fmt(duration)}</span>
          </div>
        </div>
      )}

      {/* Queue list */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-sm opacity-60 text-center py-8">Laddar…</p>
        ) : items.length === 0 ? (
          <div className="neo-card p-6 text-center bg-white">
            <p className="text-sm font-bold mb-1">Kön är tom 🌸</p>
            <p className="text-xs opacity-70">Ta foto eller ladda upp papper, så lägger vi till dem här i tur och ordning.</p>
          </div>
        ) : items.map((it) => (
          <QueueItem
            key={it.id}
            item={it}
            isCurrent={it.id === currentId}
            isDragOver={dragOverId === it.id}
            onPlay={() => playItem(it.id)}
            onTogglePlayed={() => togglePlayed(it.id, !it.played)}
            onRemove={() => removeItem(it.id)}
            onDragStart={() => handleDragStart(it.id)}
            onDragOver={(e) => handleDragOver(e, it.id)}
            onDrop={(e) => handleDrop(e, it.id)}
          />
        ))}
      </div>

      {items.length > 0 && (
        <div className="flex gap-2 justify-end">
          <button
            onClick={clearPlayed}
            className="neo-btn px-3 py-1.5 bg-white text-xs"
            data-testid="queue-clear-played-btn"
          >
            <Check className="w-3 h-3 inline" /> Rensa spelade
          </button>
          <button
            onClick={clearAll}
            className="neo-btn px-3 py-1.5 bg-[#FFE8E8] text-xs"
            data-testid="queue-clear-all-btn"
          >
            <Trash2 className="w-3 h-3 inline" /> Töm allt
          </button>
        </div>
      )}

      {cameraOpen && (
        <CameraModal
          onClose={() => setCameraOpen(false)}
          onCapture={async (dataUrl) => {
            setCameraOpen(false);
            await ocrAndAdd(dataUrl);
          }}
        />
      )}
    </div>
  );
}

function QueueItem({ item, isCurrent, isDragOver, onPlay, onTogglePlayed, onRemove, onDragStart, onDragOver, onDrop }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`neo-card p-2.5 flex items-start gap-2 transition-all ${isDragOver ? 'ring-2 ring-[#D06892]' : ''} ${isCurrent ? 'bg-[#FFF5FA]' : item.played ? 'bg-[#F4F4F4] opacity-70' : 'bg-white'}`}
      data-testid={`queue-item-${item.id}`}
    >
      <span className="cursor-grab active:cursor-grabbing pt-0.5 opacity-50" title="Dra för att ändra ordning">
        <GripVertical className="w-4 h-4" />
      </span>
      <button
        onClick={onTogglePlayed}
        className={`mt-0.5 w-5 h-5 border-2 border-black rounded-full flex items-center justify-center shrink-0 ${item.played ? 'bg-[#B9FBC0]' : 'bg-white'}`}
        data-testid={`queue-toggle-played-${item.id}`}
        title={item.played ? 'Markera olästt' : 'Markera spelad'}
      >
        {item.played && <Check className="w-3 h-3" strokeWidth={3} />}
      </button>
      <div className="flex-1 min-w-0">
        <button onClick={() => setExpanded((x) => !x)} className="text-left w-full" data-testid={`queue-expand-${item.id}`}>
          <p className={`text-sm font-bold truncate ${item.played ? 'line-through' : ''}`}>{item.title}</p>
          <p className="text-[11px] opacity-60 truncate">{item.char_count} tecken · {(item.text || '').slice(0, 80)}</p>
        </button>
        {expanded && (
          <p className="mt-2 text-xs whitespace-pre-wrap bg-[#FFFEF5] p-2 rounded border-2 border-black max-h-40 overflow-auto">{item.text}</p>
        )}
      </div>
      <button
        onClick={onPlay}
        className={`neo-btn p-1.5 ${isCurrent ? 'bg-[#FFC8DD]' : 'bg-white'}`}
        title={isCurrent ? 'Spelas nu' : 'Spela detta'}
        data-testid={`queue-play-item-${item.id}`}
      >
        <Play className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onRemove}
        className="neo-btn p-1.5 bg-white"
        title="Ta bort"
        data-testid={`queue-remove-${item.id}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function CameraModal({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  const startCam = useCallback(async (mode) => {
    setError(null);
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) { setError(e?.message || 'Kunde inte starta kameran'); }
  }, []);

  useEffect(() => {
    startCam(facingMode);
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [facingMode, startCam]);

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(v, 0, 0, w, h);
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    if (scale < 1) {
      const c2 = document.createElement('canvas');
      c2.width = Math.round(w * scale); c2.height = Math.round(h * scale);
      c2.getContext('2d').drawImage(canvas, 0, 0, c2.width, c2.height);
      onCapture(c2.toDataURL('image/jpeg', 0.85));
    } else {
      onCapture(canvas.toDataURL('image/jpeg', 0.85));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="queue-camera-modal">
      <div className="neo-card bg-white p-3 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-2">
          <p className="font-bold">📸 Lägg till papper i kön</p>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="queue-camera-close"><X className="w-4 h-4" /></button>
        </div>
        {error ? (
          <div className="p-4 text-sm bg-[#FFE8E8] rounded-xl border-2 border-black">
            Kameran är blockerad: {error}.
          </div>
        ) : (
          <video ref={videoRef} className="w-full rounded-xl border-2 border-black bg-black aspect-video object-cover" muted playsInline />
        )}
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))} className="neo-btn p-2 bg-white text-xs">🔄 Byt kamera</button>
          <button onClick={snap} disabled={!!error} className="neo-btn flex-1 py-3 bg-[#FFC8DD] font-bold" data-testid="queue-camera-snap">📸 Ta bilden</button>
        </div>
      </div>
    </div>
  );
}
